import { TitleCasePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, signal } from "@angular/core";
import {
  AudienceTargeting,
  CampaignType,
  Marketplace,
  MatchType,
  StrategyTactic,
  TacticType,
} from "@front/m19-api-client";
import { SegmentEx, StrategyEx, SupportedAudienceMatchType } from "@front/m19-models";
import { SdStrategyService, SegmentService } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonDirective,
  ICardComponent,
  ICardContent,
  IIconComponent,
  ITooltipDirective,
  MODAL_DATA,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  ProductSegmentModalComponent,
  ProductSegmentModalData,
} from "@m19-board/segments/product-segment-modal.component";
import { RemoveTacticModalData } from "@m19-board/strategies/tactic/tactic.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { RemoveTacticModalComponent } from "@m19-board/strategies/tactic/remove-tactic-modal.component";

export interface RemoveAudienceModalData {
  accountId: string;
  marketplace: Marketplace;
  strategyId: number;
  audience: AudienceTargeting;
}

/* Modal component to delete the Audience from the Strategy */
@Component({
  templateUrl: "./remove-audience-popup.component.html",
  imports: [TranslocoDirective, IButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalRemoveAudienceFromStrategyComponent {
  private sdStrategyService = inject(SdStrategyService);
  private toastrService = inject(ToasterService);
  private translocoService = inject(TranslocoService);

  protected readonly modalData = inject(MODAL_DATA) as RemoveAudienceModalData;
  modalRef = inject(ModalRef);

  delete() {
    this.sdStrategyService
      .removeRemarketingAudienceFromSdStrategy(
        this.modalData.accountId,
        this.modalData.marketplace,
        this.modalData.strategyId,
        this.modalData.audience.audienceTargetId!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("sd-targeting.audience_removed"));
          this.modalRef.close();
        },
        error: (error) => {
          this.toastrService.error(error, this.translocoService.translate("sd-targeting.audience_removal_failed"));
        },
      });
    this.modalRef.close();
  }
}

export interface RemoveTargetingModalData {
  accountId: string;
  strategyId: number;
  marketplace: Marketplace;
  organizationId: number;
}

@Component({
  templateUrl: "./remove-ia-powered-targeting-popup.component.html",
  imports: [TranslocoDirective, IButtonDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalRemoveAIPoweredTargetingFromStrategyComponent {
  private strategyService = inject(SdStrategyService);
  private toastrService = inject(ToasterService);
  private translocoService = inject(TranslocoService);

  modalData = inject(MODAL_DATA) as RemoveTargetingModalData;
  modalRef = inject(ModalRef);

  delete() {
    this.strategyService
      .updateStrategyAutoAlgoExploration(
        this.modalData.accountId,
        this.modalData.marketplace,
        this.modalData.organizationId,
        this.modalData.strategyId,
        false,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("sd-targeting.ai-powered_targeting_removed"));
          this.modalRef.close();
        },
        error: (error) => {
          this.toastrService.error(error);
        },
      });
  }
}

@Component({
  selector: "app-sd-targeting",
  templateUrl: "./sd-targeting.component.html",
  styleUrls: ["./sd-targeting.component.scss"],
  imports: [
    TranslocoDirective,
    ICardComponent,
    IBadgeComponent,
    ITooltipDirective,
    TitleCasePipe,
    ICardContent,
    IIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdTargetingComponent implements OnInit {
  private modalService = inject(ModalService);
  private segmentService = inject(SegmentService);
  private translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly TacticType = TacticType;
  readonly supportedAudienceMatchType = SupportedAudienceMatchType;

  tactic = input<StrategyTactic>();

  audience = input<AudienceTargeting>();

  strategy = input.required<StrategyEx>();

  organizationId = input.required<number>();

  isReadOnly = input.required<boolean>();

  defaultTactic = input<boolean>(false);

  isLastTargeting = input<boolean | undefined>(undefined);

  segment = signal<SegmentEx | undefined>(undefined);

  ngOnInit(): void {
    if (this.tactic()?.segmentId) {
      this.segmentService
        .getSegments(this.strategy().accountId, this.strategy().marketplace)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((segments) => {
          this.segment.set(segments.get(this.tactic()!.segmentId!));
        });
    }
  }

  nbProductTargeting(): number {
    const segment = this.segment();
    if (!segment) {
      return 0;
    }
    return segment.items.filter((x) => x.matchType == MatchType.asinSameAs).length ?? 0;
  }

  editSegment() {
    this.modalService.openModal<ProductSegmentModalData, void>(ProductSegmentModalComponent, {
      modalTitle: this.translocoService.translate("segment-manager.edit_segment_tt", [""]),
      data: {
        segment: this.segment(),
        accountId: this.strategy().accountId,
        marketplace: this.strategy().marketplace,
        isReadOnly: this.isReadOnly(),
      },
    });
  }

  // AI-powered targeting
  disableAutoAlgoExploration() {
    this.modalService.openModal<RemoveTargetingModalData, void>(ModalRemoveAIPoweredTargetingFromStrategyComponent, {
      size: "md",
      modalTitle: this.translocoService.translate(
        "remove-ia-powered-targeting-popup.remove_ai-powered_targeting_from_strategy",
      ),
      data: {
        accountId: this.strategy().accountId,
        strategyId: this.strategy().strategyId,
        marketplace: this.strategy().marketplace,
        organizationId: this.organizationId(),
      },
    });
  }

  removeTacticFromStrategy() {
    this.modalService.openModal<RemoveTacticModalData, void>(RemoveTacticModalComponent, {
      modalTitle: this.translocoService.translate("remove-tactic-popup.remove_targeting_from_strategy"),
      data: {
        accountId: this.strategy().accountId,
        marketplace: this.strategy().marketplace,
        strategyId: this.strategy().strategyId,
        tactic: this.tactic()!,
        segment: this.segment()!,
        isSdTargeting: true,
        campaignType: CampaignType.SD,
      },
    });
  }

  removeAudienceFromStrategy() {
    this.modalService.openModal<RemoveAudienceModalData, void>(ModalRemoveAudienceFromStrategyComponent, {
      modalTitle: this.translocoService.translate("remove-audience-popup.remove_audience_targeting_from_strategy"),
      data: {
        accountId: this.strategy().accountId,
        marketplace: this.strategy().marketplace,
        strategyId: this.strategy().strategyId,
        audience: this.audience()!,
      },
    });
  }
}
